import { useEffect, useMemo, useState } from "react";

/**
 * Client-side pagination for sourcing lists.
 *
 * The sourcing APIs return the full result set in one call (unlike the interview
 * endpoints, which page server-side), so we slice locally and feed the same
 * shared <Pagination /> control the interview table uses.
 *
 * @param {Array}  items      the already-filtered/sorted list to page through
 * @param {Object} options
 * @param {number} options.initialPageSize  rows per page on first render
 * @param {*}      options.resetKey         changes to this send the user back to page 1
 *                                          (pass a string built from the active filters)
 */
export default function usePagination(items = [], { initialPageSize = 10, resetKey = null } = {}) {
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(initialPageSize);

    const totalItems = items.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));

    // Filters/search/sort changed — the old page number no longer means anything.
    useEffect(() => {
        setCurrentPage(1);
    }, [resetKey, itemsPerPage]);

    // The list can shrink under us (unsave, shortlist-removes-row, refetch).
    // Without this the user is stranded on an empty page.
    useEffect(() => {
        if (currentPage > totalPages) setCurrentPage(totalPages);
    }, [currentPage, totalPages]);

    const pageItems = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return items.slice(start, start + itemsPerPage);
    }, [items, currentPage, itemsPerPage]);

    const handlePageChange = (page) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
    };

    const handleItemsPerPageChange = (size) => {
        setItemsPerPage(size);
        setCurrentPage(1);
    };

    return {
        currentPage,
        itemsPerPage,
        totalItems,
        totalPages,
        pageItems,
        handlePageChange,
        handleItemsPerPageChange,
        setCurrentPage,
    };
}
